import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Checkbox } from '../components/ui/checkbox';
import { Progress } from '../components/ui/progress';
import api from '../lib/api';
import { toast } from 'sonner';
import { ArrowRight, ArrowLeft, CheckCircle } from 'lucide-react';

const STEPS = ['Vos coordonnées', 'Votre situation', 'Vos besoins', 'Confirmation'];

export default function StayRequestPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    first_name: '', last_name: '', email: '', phone: '',
    request_type: 'sejour_repit', location_preference: '', preferred_period: '',
    general_situation: '', mobility_needs: '', assistance_needs: '',
    coordination_needs: '', indicative_budget: '', message: '',
    callback_requested: false, rgpd_consent: false
  });

  const u = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.rgpd_consent) { toast.error('Veuillez accepter la politique de confidentialité'); return; }
    setLoading(true);
    try {
      await api.post('/stay-requests', form);
      navigate('/merci?type=sejour');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur lors de l\'envoi');
    } finally {
      setLoading(false);
    }
  };

  const next = () => { if (step < 3) setStep(step + 1); else handleSubmit(); };
  const prev = () => { if (step > 0) setStep(step - 1); };

  const canNext = () => {
    if (step === 0) return form.first_name && form.last_name && form.email;
    if (step === 3) return form.rgpd_consent;
    return true;
  };

  return (
    <div className="min-h-screen py-24 md:py-32 bg-background" data-testid="stay-request-page">
      <div className="max-w-2xl mx-auto px-4 sm:px-6">
        <div className="text-center mb-10">
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-foreground mb-3">Demander un séjour de répit</h1>
          <p className="text-muted-foreground">Remplissez ce formulaire et notre équipe vous recontactera sous 48h.</p>
        </div>

        {/* Progress */}
        <div className="mb-8">
          <div className="flex justify-between mb-2">
            {STEPS.map((s, i) => (
              <span key={s} className={`text-xs font-medium ${i <= step ? 'text-primary' : 'text-muted-foreground'}`}>{s}</span>
            ))}
          </div>
          <Progress value={(step + 1) / STEPS.length * 100} className="h-2" />
        </div>

        <div className="bg-white rounded-2xl p-8 shadow-[0_4px_20px_-2px_rgba(0,0,0,0.05)] border border-border/50">
          {step === 0 && (
            <div className="space-y-4" data-testid="step-coordonnees">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Prénom *</Label>
                  <Input value={form.first_name} onChange={e => u('first_name', e.target.value)} className="mt-1 rounded-xl" data-testid="stay-firstname" required />
                </div>
                <div>
                  <Label>Nom *</Label>
                  <Input value={form.last_name} onChange={e => u('last_name', e.target.value)} className="mt-1 rounded-xl" data-testid="stay-lastname" required />
                </div>
              </div>
              <div>
                <Label>Email *</Label>
                <Input type="email" value={form.email} onChange={e => u('email', e.target.value)} className="mt-1 rounded-xl" data-testid="stay-email" required />
              </div>
              <div>
                <Label>Téléphone</Label>
                <Input type="tel" value={form.phone} onChange={e => u('phone', e.target.value)} className="mt-1 rounded-xl" data-testid="stay-phone" />
              </div>
              <div className="flex items-center gap-2 pt-2">
                <Checkbox id="callback" checked={form.callback_requested} onCheckedChange={v => u('callback_requested', v)} data-testid="stay-callback" />
                <Label htmlFor="callback" className="text-sm cursor-pointer">Je souhaite être rappelé(e)</Label>
              </div>
            </div>
          )}

          {step === 1 && (
            <div className="space-y-4" data-testid="step-situation">
              <div>
                <Label>Destination souhaitée</Label>
                <Input value={form.location_preference} onChange={e => u('location_preference', e.target.value)} placeholder="Ex: Provence, Bretagne..." className="mt-1 rounded-xl" data-testid="stay-location" />
              </div>
              <div>
                <Label>Période souhaitée</Label>
                <Input value={form.preferred_period} onChange={e => u('preferred_period', e.target.value)} placeholder="Ex: Juin 2026, été 2026..." className="mt-1 rounded-xl" data-testid="stay-period" />
              </div>
              <div>
                <Label>Votre situation générale</Label>
                <Textarea value={form.general_situation} onChange={e => u('general_situation', e.target.value)} placeholder="Décrivez brièvement votre situation d'aidant..." className="mt-1 rounded-xl min-h-[100px]" data-testid="stay-situation" />
              </div>
              <div>
                <Label>Budget indicatif</Label>
                <select value={form.indicative_budget} onChange={e => u('indicative_budget', e.target.value)} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="stay-budget">
                  <option value="">Sélectionnez...</option>
                  <option value="< 1000 EUR">Moins de 1 000 EUR</option>
                  <option value="1000-2000 EUR">1 000 - 2 000 EUR</option>
                  <option value="2000-3000 EUR">2 000 - 3 000 EUR</option>
                  <option value="3000-5000 EUR">3 000 - 5 000 EUR</option>
                  <option value="> 5000 EUR">Plus de 5 000 EUR</option>
                </select>
              </div>
            </div>
          )}

          {step === 2 && (
            <div className="space-y-4" data-testid="step-besoins">
              <div>
                <Label>Besoins de mobilité</Label>
                <select value={form.mobility_needs} onChange={e => u('mobility_needs', e.target.value)} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="stay-mobility">
                  <option value="">Sélectionnez...</option>
                  <option value="Fauteuil roulant">Fauteuil roulant</option>
                  <option value="Fauteuil roulant électrique">Fauteuil roulant électrique</option>
                  <option value="Déambulateur">Déambulateur</option>
                  <option value="Canne">Canne</option>
                  <option value="Aucun besoin spécifique">Aucun besoin spécifique</option>
                  <option value="Autre">Autre</option>
                </select>
              </div>
              <div>
                <Label>Besoins d'assistance</Label>
                <select value={form.assistance_needs} onChange={e => u('assistance_needs', e.target.value)} className="mt-1 w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="stay-assistance">
                  <option value="">Sélectionnez...</option>
                  <option value="Aide au lever/coucher">Aide au lever/coucher</option>
                  <option value="Aide aux repas">Aide aux repas</option>
                  <option value="Accompagnement continu">Accompagnement continu</option>
                  <option value="Aide ponctuelle">Aide ponctuelle</option>
                  <option value="Surveillance nocturne">Surveillance nocturne</option>
                  <option value="Aucun besoin">Aucun besoin</option>
                </select>
              </div>
              <div>
                <Label>Besoins de coordination particuliers</Label>
                <Textarea value={form.coordination_needs} onChange={e => u('coordination_needs', e.target.value)} placeholder="Coordination avec professionnels de santé, traitements particuliers..." className="mt-1 rounded-xl min-h-[80px]" data-testid="stay-coordination" />
              </div>
              <div>
                <Label>Message complémentaire</Label>
                <Textarea value={form.message} onChange={e => u('message', e.target.value)} placeholder="Toute information que vous souhaitez nous communiquer..." className="mt-1 rounded-xl min-h-[80px]" data-testid="stay-message" />
              </div>
            </div>
          )}

          {step === 3 && (
            <div className="space-y-6" data-testid="step-confirmation">
              <div className="bg-background rounded-xl p-6 space-y-3 text-sm">
                <h3 className="font-heading font-semibold text-lg mb-3">Récapitulatif</h3>
                <p><span className="font-medium text-foreground">Nom :</span> <span className="text-muted-foreground">{form.first_name} {form.last_name}</span></p>
                <p><span className="font-medium text-foreground">Email :</span> <span className="text-muted-foreground">{form.email}</span></p>
                {form.phone && <p><span className="font-medium text-foreground">Téléphone :</span> <span className="text-muted-foreground">{form.phone}</span></p>}
                {form.location_preference && <p><span className="font-medium text-foreground">Destination :</span> <span className="text-muted-foreground">{form.location_preference}</span></p>}
                {form.preferred_period && <p><span className="font-medium text-foreground">Période :</span> <span className="text-muted-foreground">{form.preferred_period}</span></p>}
                {form.mobility_needs && <p><span className="font-medium text-foreground">Mobilité :</span> <span className="text-muted-foreground">{form.mobility_needs}</span></p>}
                {form.assistance_needs && <p><span className="font-medium text-foreground">Assistance :</span> <span className="text-muted-foreground">{form.assistance_needs}</span></p>}
              </div>
              <div className="flex items-start gap-2">
                <Checkbox id="rgpd" checked={form.rgpd_consent} onCheckedChange={v => u('rgpd_consent', v)} data-testid="stay-rgpd" />
                <Label htmlFor="rgpd" className="text-sm cursor-pointer leading-relaxed">
                  J'accepte que mes données soient traitées par M-CareLink dans le cadre de ma demande, conformément à la{' '}
                  <a href="/confidentialite" className="text-primary underline" target="_blank">politique de confidentialité</a>.
                </Label>
              </div>
            </div>
          )}

          {/* Navigation */}
          <div className="flex justify-between mt-8 pt-6 border-t border-border/30">
            {step > 0 ? (
              <Button variant="outline" onClick={prev} className="rounded-full" data-testid="stay-prev-btn">
                <ArrowLeft className="mr-2 w-4 h-4" /> Précédent
              </Button>
            ) : <div />}
            <Button onClick={next} disabled={!canNext() || loading} className="rounded-full bg-accent text-accent-foreground hover:bg-accent/90 hover:scale-105 transition-all" data-testid="stay-next-btn">
              {step === 3 ? (loading ? 'Envoi...' : 'Envoyer ma demande') : 'Suivant'}
              {step < 3 ? <ArrowRight className="ml-2 w-4 h-4" /> : <CheckCircle className="ml-2 w-4 h-4" />}
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}
